/* Reference data for the prototype.
 *
 * In the real build every export here is a row in Snowflake — IT_REF_CATEGORY,
 * IT_REF_REQUEST_TYPE (form schema in a VARIANT column), IT_REF_SLA_POLICY,
 * IT_PRIORITY_RULES — served by a cached GET /api/reference. It lives in a file
 * for now so the flow can be walked through and argued with before any DDL is
 * written. The SHAPES here are the shapes the tables will take, which is the
 * whole point of showing it to people first.
 *
 * Categories, their observed shares, the Phones request types and the option
 * wording all come from the agreed `structured-intake` mockup. The remaining
 * categories' request types are drafted in the same shape — icon, plain-English
 * label, and a subtitle that says what it covers, so nobody has to guess which
 * row is theirs.
 *
 * NOTHING here is settled. It is the list to argue with.
 */

// -------------------------------------------------------------------- desks

/* The three tiles you see on the way into the portal.
 *
 * Grant, 2026-09-14: "Three tiles when they first enter the portal. IT Help,
 * Service Titan Help, AI Access."
 *
 * A desk sits ABOVE categories: you pick a desk, then you are inside that help
 * desk — its own menu, its own queue, its own staff. Before this, ServiceTitan
 * tickets sat in the same pile as broken laptops and there was nothing
 * AI-shaped in the catalogue at all.
 *
 * WHY THIS IS DATA AND NOT THREE COMPONENTS. Same argument as
 * TECH_QUICK_LINKS: in the real build a desk is a row in IT_REF_DESK and a
 * category carries a DESK_KEY, so moving ServiceTitan's licence work onto a
 * different desk is a seed re-run, not a deploy. It also lets one test prove
 * the mapping is whole — a category with no desk is a category nobody can
 * reach, and it would not throw, it would just quietly vanish.
 *
 * `roomHint` is NOT a room key and is never gated on. It is a note to whoever
 * authors the rooms in the Hub, because the Hub is the sole author of room
 * keys — this repo reads them back into access.rooms.generated.js and never
 * invents one. See the hub-access-wiring skill.
 */
export const DESKS = [
  {
    id: 'it-help',
    label: 'IT Help',
    icon: 'support_agent',
    blurb: 'Phones, laptops, logins, Wi-Fi',
    hint: 'Anything broken, or anything you need',
    roomHint: 'IT Help',
  },
  {
    id: 'servicetitan-help',
    label: 'ServiceTitan Help',
    icon: 'build_circle',
    blurb: 'Licences, price book, sign-in',
    hint: "Rhea's team picks these up",
    roomHint: 'ServiceTitan Help',
  },
  {
    id: 'ai-access',
    label: 'AI Access',
    icon: 'auto_awesome',
    blurb: 'Copilot, Claude, getting started',
    hint: 'Access to AI tools, and help using them',
    roomHint: 'AI Access',
  },
];

// ---------------------------------------------------------------- categories

// Shares are the observed split across the 100 most recent tickets on the live
// Asana Ticketing board (2026-08-14). Ten is at the very top of what a person
// will scan — "Microsoft 365" folds into Accounts & access and "App on my
// phone" into Devices if eight is wanted.
//
// `deskId` decides which tile a category lives behind. Every category needs
// one; catalog.test.js fails the build if any is missing or points nowhere.
export const CATEGORIES = [
  { id: 'phones',       deskId: 'it-help',           label: 'Phones & calls',    icon: 'call',            blurb: 'Zoom, queues, routing',        share: '~30% of tickets', hot: true },
  { id: 'accounts',     deskId: 'it-help',           label: 'Accounts & access', icon: 'key',             blurb: 'Mailboxes, files, logins',     share: '~25%' },
  { id: 'devices',      deskId: 'it-help',           label: 'Devices',           icon: 'laptop_mac',      blurb: 'Laptop, monitor, printer',     share: '~18%' },
  { id: 'servicetitan', deskId: 'servicetitan-help', label: 'ServiceTitan',      icon: 'build_circle',    blurb: 'Licences and price book',      share: '~10%' },
  { id: 'network',      deskId: 'it-help',           label: 'Network',           icon: 'wifi',            blurb: 'Wi-Fi, internet dropping',     share: '~7%' },
  { id: 'm365',         deskId: 'it-help',           label: 'Microsoft 365',     icon: 'apps',            blurb: 'Outlook, Excel, OneDrive',     share: '~6%' },
  { id: 'mobileapps',   deskId: 'it-help',           label: 'App on my phone',   icon: 'smartphone',      blurb: 'Teams, Rilla, eFleets',        share: '~6%' },
  { id: 'security',     deskId: 'it-help',           label: 'Security',          icon: 'shield',          blurb: 'Hacked, phishing',             share: '~2%' },
  { id: 'facilities',   deskId: 'it-help',           label: 'Facilities & AV',   icon: 'meeting_room',    blurb: 'Key cards, desks, rooms',      share: '~4%' },
  { id: 'hardware',     deskId: 'it-help',           label: 'New hardware',      icon: 'devices_other',   blurb: 'Needs manager approval',       share: '—' },
  // The AI Access desk's only category. Brand new — nobody has raised an AI
  // ticket yet, so the five types behind it are a GUESS, deliberately, the same
  // way the six technician buttons were. Changing it is a catalog.js edit plus
  // `npm run seed:build`, which is the whole reason it is data.
  { id: 'ai',           deskId: 'ai-access',         label: 'AI tools',          icon: 'auto_awesome',    blurb: 'Copilot, Claude, using them',  share: 'new' },
  // IT asked for this on the first page. It is the escape hatch: nothing on the
  // list fits, so say what you need in your own words and a human reads it.
  //
  // The risk is real and named — "Other" is exactly what swallowed half the
  // Asana board. The difference here is that it is LAST, every other door is
  // specific and labelled, and Other still captures request-vs-fault and when
  // it is needed by, so triage is not blind. When Other starts filling up, that
  // is the signal to add a request type, not to leave it.
  //
  // It sits on IT Help because IT triages anything unrecognised and can move it.
  { id: 'other',        deskId: 'it-help',           label: 'Something else',    icon: 'help_center',     blurb: "Not on this list? Tell us",    share: '—' },
];

// ------------------------------------------------------------- shared fields

/* Two kinds of thing arrive at a help desk, and they need different questions.
 *
 *   kind: 'fault'   — something is broken. How often, since when, and who else
 *                     is affected are exactly the right questions, and they are
 *                     what the priority rules read.
 *   kind: 'request' — nothing is broken; someone is asking for something. "How
 *                     often does the price book update happen?" is nonsense.
 *                     What matters is when they need it by.
 *
 * Grant, 2026-08-14: the triage three "doesn't make any sense for request
 * something or service titan". Correct — they were being bolted onto every type
 * regardless. `kind` is now on every request type and decides which set applies.
 */

// Asked on FAULTS only.
const TRIAGE_FIELDS = [
  {
    key: 'frequency',
    label: 'How often?',
    type: 'choice',
    required: true,
    options: [
      { value: 'once',      label: 'Once' },
      { value: 'few_daily', label: 'A few times a day' },
      { value: 'every',     label: 'Every time' },
      { value: 'constant',  label: "Constantly, can't work" },
    ],
  },
  {
    key: 'since',
    label: 'Since when?',
    type: 'choice',
    required: true,
    options: [
      { value: 'today',     label: 'Today' },
      { value: 'this_week', label: 'This week' },
      { value: 'longer',    label: 'Longer than a week' },
    ],
  },
  {
    key: 'othersAffected',
    label: 'Anyone else affected that you know of?',
    type: 'choice',
    required: true,
    options: [
      { value: 'just_me',      label: 'Just me' },
      { value: 'my_team',      label: 'Others on my team' },
      { value: 'whole_office', label: 'Whole office' },
      { value: 'dont_know',    label: "Don't know" },
    ],
  },
];

// Asked on REQUESTS only. One question, because a request that nobody is blocked
// by mostly needs a date — and it is a real rules input, not decoration.
const REQUEST_FIELDS = [
  {
    key: 'neededBy',
    label: 'When do you need it by?',
    type: 'choice',
    required: true,
    options: [
      { value: 'today',      label: 'Today' },
      { value: 'this_week',  label: 'This week' },
      { value: 'this_month', label: 'This month' },
      { value: 'no_rush',    label: 'No particular date' },
    ],
  },
];

// Every form ends with this. Read by whoever picks the ticket up; NEVER used to
// categorise, route or prioritise — that is the entire point of the structured
// intake above it.
export const NOTE_FIELD = {
  key: 'note',
  label: 'Anything else that would help?',
  type: 'textarea',
  required: false,
  maxLength: 2000,
};

const choice = (key, label, options, extra = {}) => ({
  key,
  label,
  type: 'choice',
  required: true,
  options: options.map((o) => (typeof o === 'string' ? { value: slug(o), label: o } : o)),
  ...extra,
});

const text = (key, label, extra = {}) => ({ key, label, type: 'text', required: true, ...extra });

/* A "check all that apply" question. The answer is an ARRAY of values, which is
 * the one field type whose empty state is `[]` rather than `''` - every
 * required-check has to use `hasAnswer()` below rather than a bare falsy test,
 * or an empty array reads as answered. */
const multi = (key, label, options, extra = {}) => ({
  key,
  label,
  type: 'multi',
  required: true,
  options: options.map((o) => (typeof o === 'string' ? { value: slug(o), label: o } : o)),
  ...extra,
});

/* ---------------------------------------------------------------------------
 * CONDITIONAL FIELDS, AND WHY THE LOGIC LIVES HERE.
 *
 * A form that asks everyone every question is the thing the conversational bot
 * was meant to avoid. `showIf` brings the skipping back without the drift: it is
 * a data rule right here in the catalogue rather than a branch inside a
 * component, so one file describes the whole form. Restating it in the
 * component would put the two out of step the first time either changed.
 *
 *   showIf: { field: 'gate', equals: 'yes' }
 *   showIf: { field: 'uses', includes: 'automation' }   // for a multi
 *
 * A HIDDEN FIELD IS NOT AN UNANSWERED FIELD. Anything hidden must be dropped
 * from the required check, or the form silently refuses to submit and points at
 * a question nobody can see. `visibleFields` is the only correct source for
 * both rendering AND validation - never iterate raw `fields` for either.
 * --------------------------------------------------------------------------- */

export function hasAnswer(value) {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && String(value).trim() !== '';
}

export function isVisible(field, answers) {
  const rule = field.showIf;
  if (!rule) return true;
  const actual = answers ? answers[rule.field] : undefined;
  if (rule.includes !== undefined) {
    return Array.isArray(actual) && actual.includes(rule.includes);
  }
  /* ANY of several - for a multi where more than one answer leads to the same
   * follow-up. `includes` asks about one box; this asks whether they ticked
   * any of a set, which is what "are you making documents of some kind?" is. */
  if (rule.includesAny !== undefined) {
    return Array.isArray(actual) && rule.includesAny.some((v) => actual.includes(v));
  }
  if (rule.equals !== undefined) return actual === rule.equals;
  if (rule.in !== undefined) return Array.isArray(rule.in) && rule.in.includes(actual);
  // An unrecognised rule shows the field. Hiding on a rule nobody understands
  // would silently drop a question, which is the worse failure.
  return true;
}

/* Visibility cascades: a field whose showIf points at a field that is itself
 * hidden must also hide. Resolved by walking the list in order, which is safe
 * because a rule may only reference a field declared before it - asserted by
 * the catalogue test. */
export function visibleFields(fields, answers) {
  const shown = [];
  const live = {};
  for (const f of fields) {
    if (isVisible(f, live)) {
      shown.push(f);
      live[f.key] = answers ? answers[f.key] : undefined;
    }
  }
  return shown;
}

// A directory lookup instead of a free-text name. Backed by Microsoft Graph via
// GET /api/people — see `app/src/lib/people.js`.
//
// Free typing still submits. A contractor, a brand-new account, or a directory
// that is briefly unreachable must not be able to block a ticket, so the picker
// keeps whatever was typed when nothing is chosen.
//
// `isManager: true` marks the box that "who is this for" auto-fills from the
// chosen person's manager. Only ~2.5% of IHS accounts have a manager set in
// Entra (measured 2026-08-17), so an empty manager box is the NORMAL outcome —
// it must never look like a failure.
const person = (key, label, extra = {}) => ({
  key,
  label,
  type: 'person',
  required: true,
  ...extra,
});

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

// ------------------------------------------------------------ request types

// `fields` is the per-type form; `icon` and `blurb` are what make the list
// scannable. In the real build all of it is the VARIANT column on
// IT_REF_REQUEST_TYPE, which is why adding a request type is data entry and not
// a deploy.
/* `forSomeoneElse: true` puts the "who is this for?" switch on a form.
 *
 * Grant, 2026-08-25: *"If a tech has a broken phone, they can't submit a ticket.
 * So we should have a way that they can request the ticket for themselves or for
 * someone else."* A dispatcher or a colleague raises it instead.
 *
 * It is DATA, not a hard-coded list of form ids, for the same reason
 * TECH_QUICK_LINKS is data: the walkthroughs may well say "put it everywhere" or
 * "not on queue changes", and that must be a seed re-run rather than a deploy.
 * Seeded into IT_REF_REQUEST_TYPE.ALLOW_ON_BEHALF.
 *
 * On phones and devices only, which is Grant's call (2026-08-25) over the
 * recommendation to put it on every form. Those are the cases where the person
 * plausibly cannot type it themselves.
 */
export const REQUEST_TYPES = [
  // ---- phones — the seven from the mockup, plus phone-broken ----
  // `phone-broken` was NOT in the mockup. It was added for the technician quick
  // links (2026-08-21) because the mockup's seven are all desk problems — a tech
  // with a smashed handset had nowhere to go but "Request new hardware", four
  // taps deep in a different category.
  //
  // It is `kind: 'request'` because the outcome is a replacement device and a
  // manager's OK, so `neededBy` is the sane shared question — "how often does
  // your phone break" is not one. ARGUE ABOUT THIS in the IT walkthrough; Jake
  // and Colby may well call it a fault.
  {
    id: 'phone-broken', categoryId: 'phones', icon: 'phonelink_erase', kind: 'request', forSomeoneElse: true,
    label: 'My phone is broken or lost', blurb: 'Your manager is told at the same time',
    fields: [
      choice('what', 'What happened?', ['Dropped it', 'Cracked screen', 'Water damage', "Won't charge", 'Lost it', 'It was stolen']),
      choice('usable', 'Can you still use it at all?', ['Yes, mostly fine', 'Barely', 'No, completely dead']),
      person('manager', 'Who is your manager?', {
        isManager: true,
        fromMe: true, // prefill from the signed-in requester's own manager
        hint: 'They are told straight away. IT starts now; their OK is needed before a replacement is ordered.',
      }),
    ],
  },
  {
    id: 'phone-dropping', categoryId: 'phones', icon: 'call_end', kind: 'fault', forSomeoneElse: true,
    label: 'Calls dropping or Zoom crashing', blurb: 'Mid-call disconnects, freezes',
    fields: [
      choice('what', 'What happens?', ['Call drops mid-conversation', 'Zoom freezes or crashes', "Can't make outbound calls", 'Both']),
    ],
  },
  {
    id: 'phone-queue', categoryId: 'phones', icon: 'phone_missed', kind: 'fault', forSomeoneElse: true,
    label: 'Not receiving calls in my queue', blurb: 'In the queue, nothing rings',
    fields: [
      text('queue', 'Which queue?', { hint: 'The name you see in Zoom, or the number people dial.' }),
      choice('what', 'What happens?', ['Nothing rings at all', 'Rings once then stops', 'Goes straight to voicemail']),
    ],
  },
  {
    id: 'phone-wrongbrand', categoryId: 'phones', icon: 'alt_route', kind: 'fault', forSomeoneElse: true,
    label: 'Calls going to the wrong brand', blurb: 'Wrong greeting, wrong queue',
    fields: [
      text('expected', 'Which brand should it be?'),
      text('actual', 'Which brand is it reaching instead?'),
    ],
  },
  {
    id: 'phone-callerid', categoryId: 'phones', icon: 'dialpad', kind: 'fault', forSomeoneElse: true,
    label: 'Wrong number showing on outbound', blurb: 'Caller ID / area code',
    fields: [text('shows', 'What shows now?'), text('shouldShow', 'What should it show?')],
  },
  {
    id: 'phone-headset', categoryId: 'phones', icon: 'headset_mic', kind: 'fault', forSomeoneElse: true,
    label: 'Headset or audio problem', blurb: "Can't hear, can't be heard",
    fields: [
      choice('symptom', "What's wrong?", ["They can't hear me", "I can't hear them", 'Cutting in and out', "Won't connect"]),
    ],
  },
  {
    id: 'phone-changequeue', categoryId: 'phones', icon: 'settings_phone', kind: 'request', forSomeoneElse: true,
    label: 'Change a queue, greeting or menu', blurb: "Needs a manager's OK",
    fields: [
      text('queue', 'Which queue, greeting or menu?'),
      text('change', 'What should change?'),
      person('manager', 'Which manager has approved it?', { isManager: true }),
    ],
  },
  {
    id: 'phone-newext', categoryId: 'phones', icon: 'add_ic_call', kind: 'request', forSomeoneElse: true,
    label: 'New extension or phone number', blurb: 'For a person or a team',
    fields: [
      choice('forWho', 'Who is it for?', ['A person', 'A team or queue']),
      text('detail', 'Their name, or the team'),
    ],
  },

  // ---- accounts & access ----
  {
    id: 'account-locked', categoryId: 'accounts', icon: 'lock_reset', kind: 'fault',
    label: 'Locked out or password reset', blurb: "Can't sign in at all",
    fields: [
      choice('system', 'Which login?', ['Windows / Microsoft 365', 'ServiceTitan', 'A third-party portal', 'Something else']),
      choice('tried', 'Tried resetting it yourself?', ['Yes, it failed', "No, I couldn't find how"]),
    ],
  },
  {
    id: 'account-mailbox', categoryId: 'accounts', icon: 'mail', kind: 'request',
    label: 'Shared mailbox access', blurb: 'Read, or send as',
    fields: [
      text('mailbox', 'Which mailbox?', { hint: 'The email address of the shared mailbox.' }),
      choice('access', 'What do you need?', ['Read it', 'Read and send as it', 'Remove my access']),
    ],
  },
  {
    id: 'account-folder', categoryId: 'accounts', icon: 'folder_shared', kind: 'request',
    label: 'SharePoint or OneDrive folder', blurb: 'Files you cannot get to',
    fields: [
      text('folder', 'Which folder or site?'),
      choice('access', 'What do you need?', ['View only', 'Edit', 'Remove my access']),
    ],
  },
  {
    id: 'account-portal', categoryId: 'accounts', icon: 'captive_portal', kind: 'request',
    label: 'Third-party portal login', blurb: 'Supplier and vendor sites',
    fields: [
      text('portal', 'Which portal?', { hint: 'e.g. Home Depot Pro, Ferguson, a supplier site.' }),
      choice('need', 'What do you need?', ['A new login', 'Reset an existing login', 'My access changed']),
    ],
  },
  {
    id: 'account-alias', categoryId: 'accounts', icon: 'alternate_email', kind: 'request',
    label: 'Email alias or distribution list', blurb: 'Who receives what',
    fields: [
      text('address', 'Which address?'),
      choice('need', 'What do you need?', ['Add someone', 'Remove someone', 'Create a new one']),
    ],
  },

  // ---- devices ----
  {
    id: 'device-computer', categoryId: 'devices', icon: 'laptop_mac', kind: 'fault', forSomeoneElse: true,
    label: 'Laptop or desktop problem', blurb: 'Slow, crashing, or dead',
    /* A manager may raise it for someone below them (2026-10-21): the server checks the
     * reporting line in Paylocity on every save (api/src/shared/orgLine.js). */
    fields: [
      choice('symptom', "What's it doing?", ['Very slow', "Won't start", 'Crashing or freezing', 'Battery or charging', 'Something else']),
      text('assetTag', 'Asset tag or serial', { required: false, hint: 'Usually a sticker underneath. Skip it if you cannot find it.' }),
    ],
  },
  {
    id: 'device-monitor', categoryId: 'devices', icon: 'desktop_windows', kind: 'fault', forSomeoneElse: true,
    label: 'Monitor or docking station', blurb: 'Screens and docks',
    fields: [choice('symptom', "What's wrong?", ['No picture', 'Flickering', 'Dock not connecting', 'Only one screen works'])],
  },
  {
    id: 'device-printer', categoryId: 'devices', icon: 'print', kind: 'fault', forSomeoneElse: true,
    label: 'Printer or scanner', blurb: 'Nothing coming out',
    fields: [
      text('printer', 'Which printer?', { hint: 'The name on it, or where it lives.' }),
      choice('symptom', "What's wrong?", ['Not printing', 'Print quality', 'Cannot scan', 'Paper jam / hardware']),
    ],
  },
  {
    id: 'device-tablet', categoryId: 'devices', icon: 'tablet_mac', kind: 'fault', forSomeoneElse: true,
    label: 'Tablet', blurb: 'Field tablets and iPads',
    fields: [choice('symptom', "What's wrong?", ['Will not turn on', 'Very slow', 'App problem', 'Screen damage'])],
  },
  {
    id: 'device-other', categoryId: 'devices', icon: 'cable', kind: 'fault', forSomeoneElse: true,
    label: 'Something else plugged in', blurb: 'Keyboard, mouse, card reader',
    fields: [text('what', 'What is it?')],
  },

  // ---- servicetitan ----
  // New users, leavers and dispatch board access belong to the HR portal's
  // joiner/leaver path — see the spec's "HR portal boundary".
  //
  // `st-signin` was added for the technician quick links (2026-08-21). The
  // category previously held only the two REQUEST types below, so "I can't get
  // into ServiceTitan" — the second-most-likely thing a tech taps — had no door
  // of its own and landed in `account-locked` under a different category.
  // Rhea's walkthrough settles the wording and whether this is one type or three.
  {
    id: 'st-signin', categoryId: 'servicetitan', icon: 'no_accounts', kind: 'fault',
    label: "Can't sign into ServiceTitan", blurb: 'Password, licence, or the app',
    fields: [
      choice('message', 'What does it say?', [
        'Wrong username or password',
        'Account locked',
        'No licence assigned',
        'The app crashes',
        'It just spins forever',
        'Something else',
      ]),
      choice('where', 'Where are you trying?', ['ServiceTitan app on my phone', 'Tablet', 'A web browser']),
    ],
  },
  {
    id: 'st-licence', categoryId: 'servicetitan', icon: 'badge', kind: 'request',
    label: 'Licence change for existing staff', blurb: 'Someone already employed',
    fields: [
      person('who', 'Who is it for?', {
        hint: 'Search for them by name. For a NEW starter, use the HR portal instead.',
      }),
      choice('change', 'What change?', ['Add a licence', 'Remove a licence', 'Change licence type']),
      text('reason', 'Why?'),
    ],
  },
  {
    id: 'st-pricebook', categoryId: 'servicetitan', icon: 'sell', kind: 'request',
    label: 'Price book update', blurb: 'Items, prices, bulk changes',
    fields: [
      choice('change', 'What kind of update?', ['Add an item', 'Change a price', 'Retire an item', 'Bulk update']),
      text('detail', 'Which item or category?'),
    ],
  },

  // ---- network ----
  {
    id: 'network-wifi', categoryId: 'network', icon: 'wifi_off', kind: 'fault',
    label: 'Wi-Fi dropping', blurb: 'Connected but nothing loads',
    fields: [
      text('location', 'Which location or area of the building?'),
      choice('devices', 'On which devices?', ['Just mine', 'Several devices', 'Everything']),
    ],
  },
  {
    id: 'network-down', categoryId: 'network', icon: 'cloud_off', kind: 'fault',
    label: 'Internet down at a location', blurb: 'A whole site is offline',
    fields: [
      text('location', 'Which location?'),
      choice('scope', 'How much is out?', ['The whole office', 'Part of the office', 'Not sure']),
    ],
  },

  // ---- microsoft 365 ----
  {
    id: 'm365-outlook', categoryId: 'm365', icon: 'mail', kind: 'fault',
    label: 'Outlook', blurb: 'Mail and calendar',
    fields: [choice('symptom', "What's wrong?", ['Not sending', 'Not receiving', 'Will not open', 'Calendar problem', 'Something else'])],
  },
  {
    id: 'm365-office', categoryId: 'm365', icon: 'table_chart', kind: 'fault',
    label: 'Excel, Word or PowerPoint', blurb: 'Crashes and licence messages',
    fields: [text('app', 'Which one?'), choice('symptom', "What's wrong?", ['Crashing', 'A file will not open', 'Feature not working', 'Licence message'])],
  },
  {
    id: 'm365-teams', categoryId: 'm365', icon: 'groups', kind: 'fault',
    label: 'Teams on my computer', blurb: 'Sign-in, audio, missing chats',
    fields: [choice('symptom', "What's wrong?", ['Will not sign in', 'Audio or video', 'Missing a team or chat', 'Very slow'])],
  },
  {
    id: 'm365-onedrive', categoryId: 'm365', icon: 'cloud_sync', kind: 'fault',
    label: 'OneDrive sync', blurb: 'Files not where they should be',
    fields: [choice('symptom', "What's wrong?", ['Files not syncing', 'Out of space', 'Cannot find a file', 'Sync errors'])],
  },

  // ---- app on my phone ----
  {
    id: 'app-mobile', categoryId: 'mobileapps', icon: 'phone_iphone', kind: 'fault',
    label: 'An app on my phone', blurb: 'Teams, Rilla, eFleets, ServiceTitan',
    fields: [
      choice('app', 'Which app?', ['Teams', 'Outlook', 'Rilla', 'eFleets', 'Home Depot', 'ServiceTitan', 'Something else']),
      choice('symptom', "What's wrong?", ['Will not sign in', 'Crashes', 'Will not install or update', 'Missing something I should see']),
      choice('phone', 'What phone?', ['Company iPhone', 'Company Android', 'My own phone']),
    ],
  },

  // ---- security ----
  // Deliberately the shortest forms in the catalogue. Someone reporting a
  // phishing email should not be answering five questions first.
  {
    id: 'sec-phishing', categoryId: 'security', icon: 'report', kind: 'fault',
    label: 'Suspicious email or message', blurb: 'Report it, do not forward it',
    fields: [
      choice('action', 'Did you click anything or reply?', ['No', 'I clicked a link', 'I entered my password', 'I replied']),
    ],
  },
  {
    id: 'sec-compromised', categoryId: 'security', icon: 'gpp_maybe', kind: 'fault',
    label: 'I think an account is compromised', blurb: 'Tell us straight away',
    fields: [
      text('account', 'Which account?'),
      choice('why', 'What makes you think so?', ['Login alert I did not recognise', 'Emails sent that I did not send', 'Password stopped working', 'Someone told me']),
    ],
  },

  // ---- facilities & AV ----
  {
    id: 'fac-keycard', categoryId: 'facilities', icon: 'badge', kind: 'request',
    label: 'Key card or door access', blurb: 'Getting into the building',
    fields: [text('door', 'Which door or building?'), choice('need', 'What do you need?', ['A new card', 'My card stopped working', 'Access to somewhere new'])],
  },
  {
    id: 'fac-move', categoryId: 'facilities', icon: 'move_down', kind: 'request',
    label: 'Desk or office move', blurb: 'Kit that needs moving with you',
    fields: [text('from', 'Moving from where?'), text('to', 'Moving to where?'), text('when', 'When?')],
  },
  {
    id: 'fac-room', categoryId: 'facilities', icon: 'cast', kind: 'fault',
    label: 'Meeting room kit', blurb: 'TV, camera, room audio',
    fields: [text('room', 'Which room?'), choice('symptom', "What's wrong?", ['TV or display', 'Camera', 'Audio', 'Cannot join a meeting'])],
  },

  // ---- new hardware ----
  {
    id: 'hw-request', categoryId: 'hardware', icon: 'add_shopping_cart', kind: 'request',
    label: 'Request new hardware', blurb: 'Your manager approves it first',
    fields: [
      choice('item', 'What do you need?', ['Laptop', 'Monitor', 'Phone', 'Tablet', 'Headset', 'Printer', 'Accessory']),
      choice('why', 'Why?', ['New to the role', 'Existing one is broken', 'Existing one is too old', 'Additional / second device']),
      person('manager', 'Who is your manager?', {
        isManager: true,
        fromMe: true, // prefill from the signed-in requester's own manager
        hint: 'They approve it before IT orders anything.',
      }),
      choice('loaner', 'Need something in the meantime?', ['No', 'Yes, a loaner please']),
    ],
  },

  // ---- AI tools ----
  //
  // MOST OF THE AI ACCESS DESK IS A GUESS, ON PURPOSE. Nobody at IHS has raised
  // an AI ticket yet, so there is no observed volume to draft from the way the
  // Phones types were drafted from the Asana board. The four after `claude-seat`
  // are here to be argued with in the walkthroughs, exactly like the six
  // technician buttons — see the Phase D questions in BACKLOG.md.
  //
  // `claude-seat` is the exception and is NOT a guess: it is shipped, filed for
  // real, and walked through with Ryan line by line.
  //
  // THE REAL SEAT FORM, AND THE ONLY DOOR TO A CLAUDE SEAT. It sat under
  // `accounts` on the IT Help desk until 2026-09-15, while the new AI Access
  // desk opened with a thin three-field `ai-seat` stub that asked for the same
  // thing far worse. Same request, two doors, two answers. Moved here and the
  // stub deleted. Nothing routes on the category - the scoring, the assessment
  // and the API all key on TYPE_KEY 'claude-seat' - so the move is a relabel of
  // which tile it lives behind, not a change to how a seat request is handled.
  //
  // The field list below is also the SHAPE the answers are stored in: they land
  // in IT_TICKETS.FORM_DATA as named keys like every other request type and stay
  // queryable.
  //
  // A paid Claude seat is a recurring per-person cost, and most people who ask
  // for one are better served by Copilot (already paid for with M365), Kenknee,
  // or a free personal account. These questions exist to work out which - see
  // `api/src/shared/seatIntake.js` for what the assessment must learn.
  {
    id: 'claude-seat', categoryId: 'ai', icon: 'smart_toy', kind: 'request',
    label: 'Claude AI account', blurb: 'Tell us what you need it for',

    /* A FORM, not the bot. Tried conversationally first and the model would not
     * hold the script: it invented a redundant follow-up whose options did not
     * match the question before it, told people IHS templates live in Word and
     * PowerPoint (they do not), and never raised Snowflake unless the requester
     * said the word themselves. Every one of those is a question Ryan had
     * already written down exactly, in the HTML questionnaire this replaces.
     * It also cost about 18 cents a request and ~5s a question. This is free
     * and instant.
     *
     * THE SCORING IS NOT HERE. Nothing in this file decides a tier and nothing
     * sent back to a requester carries one - see api/src/shared/seatScore.js.
     */
    intakeMode: 'form',

    /* ONE QUESTION AT A TIME. Ryan, 2026-09-11: "There should only be one
     * question shown at a time." A wall of questions is what the bot was meant
     * to avoid, and showing them all at once brought it back. Only this type
     * steps - every other form is short, and its walkthrough script is written
     * against the single-page layout. */
    flow: 'stepper',

    /* THE SCREEN BEFORE THE QUESTIONS. Lifted from the standalone HTML form, in
     * Ryan's order - Kenknee FIRST, then what a seat is actually for.
     * Deliberately short: "It just can't be very long or no one will really
     * read it." */
    /* Ryan's own words, 2026-09-11, near enough verbatim - he wrote this panel
     * text himself and it is the wording the standalone HTML form already used.
     * Cost FIRST, then the cheaper options: somebody has to know a seat costs
     * something before "try Kenknee" reads as advice rather than an obstacle.
     *
     * `body` takes an array when a panel needs more than one paragraph. */
    intro: {
      title: 'Before you ask for a Claude seat',
      panels: [
        {
          tone: 'cost',
          icon: 'payments',
          title: 'Claude accounts are a real ongoing cost to IHS',
          body: 'Every seat is billed whether it is used a little or a lot. Before we assign one, we want to make sure you get the option that actually fits how you will use it, so the company is not paying for more than you need.',
        },
        {
          tone: 'info',
          icon: 'lightbulb',
          title: 'Consider this before you request a seat',
          body: [
            'For general questions about IHS data, try Kenknee first. He is our data chatbot on the BI Dashboards site and often answers exactly what you need, with no Claude seat at all. Go to hub.intermountainhs.com and open the BI Dashboards app to reach him.',
            'Copilot basic is also included with your IHS Microsoft 365 account. The basic version does not connect directly to your M365 services, but it does connect to other services and has enterprise security to keep business data safe. It covers most everyday tasks and document creation, and it even has some ChatGPT models built in that you can pick from and use for free.',
          ],
        },
      ],
      okLabel: 'Got it - continue',
      cancelLabel: 'Never mind',
      footnote: 'Answer a few quick questions and submit - IT will follow up. Takes about a minute.',
    },

    fields: [
      /* Ryan, 2026-09-11: "the use of 'real' seems weird". It also asked the
       * wrong thing - the question is about SENSITIVITY, not authenticity. And
       * it no longer gates anything: answering "no" used to hide every question
       * after it, which assumed no-company-data meant no seat. It does not -
       * somebody building slides all day needs one either way. */
      choice('gate', 'Will you use Claude with IHS information that is not already public, or with sensitive data?', [
        { value: 'yes', label: 'Yes - internal, customer, employee or financial information' },
        { value: 'no', label: 'No - general work only, nothing internal or sensitive' },
        { value: 'unsure', label: 'I am not sure yet' },
      ], {
        hint: 'Customer records, employee or payroll details, financials, and anything out of ServiceTitan or Snowflake all count. An SOP, a template, or thinking out loud before any specifics go in does not.',
      }),

      choice('frequency', 'How often will you use Claude in a typical week?', [
        { value: 'few_times', label: 'A few times a week, for quick tasks' },
        { value: 'most_days', label: 'Most work days, for regular tasks' },
        { value: 'all_day', label: 'All day, every day - it is a core tool for my job' },
      ]),

      multi('uses', 'What will you use it for?', [
        { value: 'light', label: 'Writing, quick questions, light research' },
        { value: 'sop_docs', label: 'Creating SOPs, documents or slides' },
        /* "Regular" was the wrong word and it invited the wrong answer. Ryan,
         * 2026-09-11: anything REGULAR - a report rebuilt or refreshed on a
         * schedule - should be an IT request, built once and kept working,
         * rather than a person re-doing it by hand every month with a paid
         * seat. `recurring_build` below catches that directly. */
        { value: 'regular', label: 'One-off reports, analysis, marketing work, or some coding help' },
        { value: 'design', label: 'Marketing design work (Claude Design - heavy usage)' },
        { value: 'automation', label: 'Setting up Claude to work on its own - automations, agents, tools or trackers (including Cowork)' },
        { value: 'other', label: 'Something else' },
      ]),
      text('uses_other', 'Tell us what else you would use it for', {
        showIf: { field: 'uses', includes: 'other' },
      }),
      {
        key: 'automation_warning',
        type: 'warning',
        required: false,
        showIf: { field: 'uses', includes: 'automation' },
        label: 'Automations and agents need a conversation with IT first',
        hint: 'Anything that acts on its own carries real security and data risk, so IT reviews it before it goes live. We will follow up after you submit - it does not block this request.',
      },

      /* THE QUESTION THAT MIGHT SAVE THE SEAT ENTIRELY. Ryan, 2026-09-11:
       * *"If the reason they want a claude account is because they plan on
       * creating a report or dashboard or tool or artifact that they will use
       * on a regular basis, then they just need to request it from IT rather
       * than use a claude account to do it."*
       *
       * Something used over and over should be built once, properly, and kept
       * working - not rebuilt by hand every month by whoever still has the
       * chat open. It does NOT rule a seat out on its own, which is why it
       * raises a note for IT rather than forcing a recommendation. */
      choice('recurring_build', 'Is the main thing you want a report, dashboard, tracker or tool you would use again and again?', [
        { value: 'yes', label: 'Yes - something I would re-run or refresh regularly' },
        { value: 'no', label: 'No - one-off work, or a mix of different things' },
      ], {
        hint: 'Think about whether you would be doing the same build again next month.',
      }),
      {
        key: 'recurring_warning',
        type: 'warning',
        required: false,
        showIf: { field: 'recurring_build', equals: 'yes' },
        label: 'Ask IT to build that one instead',
        hint: 'Anything you will use again and again should be built once and kept working - a dashboard on the BI site, a scheduled report, or a proper tool. Raise a request for it and IT will build it. You may still want a Claude account for other work, so carry on with this form either way.',
      },

      choice('session_shape', 'How long and complex are your typical sessions?', [
        { value: 'short', label: 'Short and simple - a few messages' },
        { value: 'medium', label: 'Medium - some back-and-forth, moderate files or data' },
        { value: 'long', label: 'Long and complex - big documents, multi-step work, or hours at a stretch' },
      ]),

      choice('snowflake', 'Do you need Snowflake access - our data warehouse?', [
        { value: 'yes', label: 'Yes - and I understand it needs a new Snowflake account' },
        { value: 'no', label: 'No - I do not need direct data warehouse access' },
      ], {
        hint: 'You probably need this if you need to ask Claude about sensitive or specific IHS data. You probably do not if you just have general data questions, or questions about an existing dashboard - Kenknee usually covers that. Most people are in the second group.',
      }),
      {
        key: 'snowflake_warning',
        type: 'warning',
        required: false,
        showIf: { field: 'snowflake', equals: 'yes' },
        label: 'Saying yes adds setup time',
        hint: 'IT has to create you a brand-new Snowflake account, and you will need to meet with IT to get everything connected.',
      },
      multi('restricted_data', 'Which restricted data do you also need?', [
        { value: 'paylocity', label: 'Paylocity (HR / payroll)' },
        { value: 'sage', label: 'Sage (accounting / financials)' },
        { value: 'neither', label: 'Neither - just the standard warehouse data' },
      ], {
        showIf: { field: 'snowflake', equals: 'yes' },
        hint: 'Each of these needs an extra restricted role grant and approval. Most people need neither.',
      }),

      multi('connectors', 'What else will you connect Claude to?', [
        { value: 'm365', label: 'Microsoft 365 (Outlook, Teams, SharePoint, Word/Excel/PowerPoint)' },
        { value: 'asana', label: 'Asana' },
        { value: 'figma', label: 'Figma' },
        { value: 'canva', label: 'Canva' },
        { value: 'zapier', label: 'Zapier' },
        { value: 'github', label: 'GitHub' },
        { value: 'none', label: 'None of these right now' },
        { value: 'other', label: 'Something else' },
      ]),
      text('connectors_other', 'Which other services?', {
        showIf: { field: 'connectors', includes: 'other' },
      }),

      /* Only worth asking of somebody who is making something. Ryan,
       * 2026-09-11: *"this question only needs to be asked if they answered a
       * previous question saying that they will be using claude for creating
       * documents or slides or reports."* Asking a person who only wants quick
       * answers whether they need IHS branding is noise, and noise is what
       * makes people stop reading a form. */
      choice('branded_docs', 'Will the work you do with Claude require it to generate IHS-branded material?', [
        { value: 'yes', label: 'Yes, often' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'no', label: 'No' },
      ], {
        showIf: { field: 'uses', includesAny: ['sop_docs', 'regular', 'design'] },
      }),

      /* Only asked of somebody who said they need branded material. Ryan,
       * 2026-09-11: *"that question should only be asked if they answer Yes,
       * often or Sometimes to question 8. If they answer No to question 8 then
       * they don't need question 9."*
       *
       * It cascades too: when `branded_docs` is itself hidden - somebody not
       * making documents at all - this hides with it, because the question is
       * about producing branded material in Word or PowerPoint. Nothing is lost
       * from the recommendation; `copilot_tried` has never fed the tier, it is
       * context for IT. */
      choice('copilot_tried', 'Have you tried accomplishing this using Copilot and an IHS template in Microsoft Word or PowerPoint?', [
        { value: 'didnt_work', label: 'Yes, and it did not do what I needed' },
        { value: 'works_ok', label: 'Yes, it works okay but I want something better' },
        { value: 'not_tried', label: 'No, I have not tried it' },
        { value: 'didnt_know', label: 'I did not know that was an option' },
      ], {
        showIf: { field: 'branded_docs', in: ['yes', 'sometimes'] },
        hint: 'Worth a try first - Copilot basic is included with your work M365 account, so it costs IHS nothing extra.',
      }),
    ],

    /* AFTER the shared questions, so the acknowledgement is genuinely the last
     * thing a person sees. `fieldsFor` appends these last. */
    finalFields: [
      {
        key: 'ack',
        type: 'ack',
        required: true,
        /* NOT Claude-specific, deliberately. Ryan, 2026-09-11: *"Maybe we don't
         * make it Claude specific and have it just say Using AI responsibly."*
         * Somebody who reads this on a seat request also uses Copilot, and a
         * personal chatbot at home. The rules are the same wherever they are,
         * and naming one tool invites "well, that was a different one".
         *
         * SHORT ON PURPOSE. A wall of policy is a wall nobody reads, and an
         * unread acknowledgement is worse than none - it looks like consent.
         * Seven lines is the ceiling; every one of them is a thing that has
         * actually gone wrong somewhere, not a general sentiment. */
        label: 'Using AI responsibly',
        points: [
          'Keep IHS information in the AI account IHS gave you. Never paste it into a free or personal AI account.',
          'Some things go in NO AI tool, including this one: card numbers, social security numbers, driving licences and medical details.',
          'Check anything before you send it or act on it. AI can be confidently wrong.',
          'Anything a customer will see gets a human read first.',
          'Do not let it decide about people - hiring, pay, reviews or discipline.',
          'AI automations, or letting AI run and act on its own, can cause serious data security problems or unintended actions. Get IT to clear it first.',
          'The account is yours and it is for IHS work - do not share the login.',
        ],
        confirmLabel: 'I understand, and I will use AI responsibly.',
      },
    ],
  },
  {
    id: 'ai-broken', categoryId: 'ai', icon: 'error', kind: 'fault',
    label: 'An AI tool is not working', blurb: "Won't load, won't sign in, errors",
    fields: [
      choice('tool', 'Which one?', ['Microsoft Copilot', 'Claude', 'ChatGPT', 'Something else']),
      text('whatHappens', 'What happens when you try?', {
        hint: 'The error message word for word, if there is one.',
      }),
    ],
  },
  {
    id: 'ai-answers', categoryId: 'ai', icon: 'psychology', kind: 'request',
    label: 'Help me get a better answer out of it', blurb: 'It works, but the answers are poor',
    fields: [
      choice('tool', 'Which one?', ['Microsoft Copilot', 'Claude', 'ChatGPT', 'Something else']),
      text('task', 'What are you trying to get it to do?'),
    ],
  },
  {
    id: 'ai-automate', categoryId: 'ai', icon: 'smart_toy', kind: 'request',
    label: 'Can AI do this job for me?', blurb: 'A task you do over and over',
    fields: [
      text('task', 'What is the job?', {
        hint: 'What you do today, and roughly how often.',
      }),
      choice('frequencyOfTask', 'How often do you do it?', [
        'Several times a day',
        'Daily',
        'Weekly',
        'Monthly or less',
      ]),
    ],
  },
  {
    id: 'ai-datacheck', categoryId: 'ai', icon: 'policy', kind: 'request',
    label: 'Is it OK to put this data into AI?', blurb: 'Check before you paste it',
    fields: [
      choice('dataKind', 'What kind of data is it?', [
        'Customer names or addresses',
        'Employee or HR data',
        'Financial data',
        'Internal documents',
        'Something else',
      ]),
      text('context', 'What do you want to do with it?'),
    ],
  },

  // ---- something else ----
  // The escape hatch IT asked for. It is the ONE place a free-text description
  // is required rather than optional, because there is nothing else to go on.
  // It is still never routed or prioritised on — a human reads it, exactly like
  // the optional note everywhere else.
  {
    id: 'other-something', categoryId: 'other', icon: 'help_center', kind: 'request',
    label: 'Something not on the list', blurb: "Say what you need and we'll sort it",
    noNote: true,
    fields: [
      choice('nature', 'Is something broken, or are you asking for something?', [
        { value: 'broken', label: 'Something is broken' },
        { value: 'asking', label: "I'm asking for something" },
      ]),
      {
        key: 'describe',
        label: 'What do you need?',
        hint: 'In your own words. This is the one place we ask you to type — a person reads it and picks it up.',
        type: 'textarea',
        required: true,
        maxLength: 2000,
      },
    ],
  },
];

// -------------------------------------------------------- tech quick links

/* The technician home's fat buttons, in observed-volume order.
 *
 * This lives here rather than hardcoded in Home.jsx for the same reason
 * everything else in this file does: in the real build it is rows in a table, so
 * changing which six buttons a tech sees is data entry, not a deploy. It also
 * means one test can prove every button points at a request type that actually
 * exists — a renamed type silently killing a tech's only door is exactly the
 * bug worth spending a test on.
 *
 * `label` is deliberately NOT the request type's own label. A tech reads
 * "Phone broken", not "My phone is broken or lost" — the button is thumb-sized
 * and the words have to fit. The type's real label is what they see next.
 *
 * `typeId: null` means "no shortcut" — open the full wizard. Exactly one entry
 * should be that, and it goes last.
 */
export const TECH_QUICK_LINKS = [
  { typeId: 'phone-broken',    icon: 'phonelink_erase', label: 'Phone broken',        hint: 'Cracked, dead, lost, stolen' },
  { typeId: 'st-signin',       icon: 'no_accounts',     label: "Can't get into ServiceTitan", hint: 'Password, licence, app' },
  { typeId: 'account-locked',  icon: 'lock_reset',      label: 'Locked out',          hint: 'Windows, email, a portal' },
  { typeId: 'phone-dropping',  icon: 'call_end',        label: 'Calls dropping',      hint: 'Cuts out mid-call' },
  { typeId: 'device-tablet',   icon: 'tablet_mac',      label: 'Tablet trouble',      hint: 'The one in the truck' },
  { typeId: null,              icon: 'more_horiz',      label: 'Something else',      hint: 'The full list' },
];

/** Where a quick link goes. Null typeId opens the wizard at step 0. */
export function quickLinkHref(link) {
  const type = link.typeId ? typeById(link.typeId) : null;
  if (!type) return '/new';
  return `/new?category=${type.categoryId}&type=${type.id}`;
}

/**
 * The whole form for a request type: its own fields, then the shared questions
 * for its KIND, then the optional note.
 *
 * Composed at read time so the shared questions can never drift out of sync
 * across the catalogue — and so a fault never gets asked when it is needed by,
 * and a request never gets asked how often it happens.
 */
/**
 * The answers, in the order they were ASKED, ready to render.
 *
 * Snowflake stores FORM_DATA as a VARIANT, and a VARIANT sorts its keys
 * alphabetically. So a receipt built from `Object.entries(formData)` read back
 * "ack, branded_docs, connectors, copilot_tried, frequency, gate, ..." - the
 * acknowledgement signed last shown first, and the first question answered
 * shown near the bottom. The catalogue is the only thing that knows the real
 * order, so the order comes from here and not from the payload.
 *
 * Anything the catalogue no longer recognises is still returned, at the end.
 * Dropping it would make an answer vanish silently the day a question is
 * renamed or retired, and a receipt that quietly omits what somebody told us
 * is worse than one with an odd-looking row on it.
 */
export function answerRows(fields, formData) {
  const data = formData || {};
  const empty = (v) => v === undefined || v === null || v === ''
    || (Array.isArray(v) && v.length === 0);
  const rows = [];
  const seen = new Set();

  for (const field of fields) {
    if (!(field.key in data) || empty(data[field.key])) continue;
    seen.add(field.key);
    rows.push({ key: field.key, field, value: data[field.key] });
  }
  for (const [key, value] of Object.entries(data)) {
    if (seen.has(key) || empty(value)) continue;
    rows.push({ key, field: undefined, value });
  }
  return rows;
}

export function fieldsFor(requestTypeId) {
  const type = REQUEST_TYPES.find((t) => t.id === requestTypeId);
  if (!type) return [];
  const shared = type.kind === 'request' ? REQUEST_FIELDS : TRIAGE_FIELDS;
  /* `finalFields` comes after EVERYTHING, including the shared questions and
   * the note. It exists for the seat form's responsible-use acknowledgement,
   * which is only meaningful as the last thing a person sees - put it in
   * `fields` and two more questions appear after someone has agreed. */
  return [
    ...type.fields,
    ...shared,
    ...(type.noNote ? [] : [NOTE_FIELD]),
    ...(type.finalFields || []),
  ];
}

export function typesFor(categoryId) {
  return REQUEST_TYPES.filter((t) => t.categoryId === categoryId);
}

export function categoryById(id) {
  return CATEGORIES.find((c) => c.id === id) || null;
}

export function typeById(id) {
  return REQUEST_TYPES.find((t) => t.id === id) || null;
}

/* ------------------------------------------------------------ desk lookups
 *
 * The desk layer's read side. Kept next to categoryById/typeById so there is
 * one obvious place to look, and so nothing outside this file ever filters on
 * `deskId` by hand — a `.filter(c => c.deskId === x)` sprinkled through the
 * pages is how a desk quietly ends up meaning two different things.
 *
 * All three return null rather than throwing on an unknown id. An unknown desk
 * arrives from a URL (`/new?desk=…`), which is user input, and the pages are
 * written to drop a bad one and fall back rather than blow up. Same call
 * already made for `?category=` and `?type=`.
 */
export function deskById(id) {
  return DESKS.find((d) => d.id === id) || null;
}

export function categoriesForDesk(deskId) {
  return CATEGORIES.filter((c) => c.deskId === deskId);
}

/** The desk a category belongs to — how a ticket learns which queue it lands in. */
export function deskForCategory(categoryId) {
  const category = categoryById(categoryId);
  return category ? deskById(category.deskId) : null;
}

// ------------------------------------------------------------- SLA policy

// Minutes. `aroundClock` ignores the working calendar — P1 does not wait for
// Monday. Everything else runs on IT's hours, 07:00–17:00 Mon–Fri, where a
// working day is 600 minutes.
export const SLA_POLICY = [
  { priority: 'P1', label: 'Critical', applies: 'Multiple people blocked, or a system down',        responseMins: 15,  resolveMins: 240,  aroundClock: true  },
  { priority: 'P2', label: 'High',     applies: 'One person blocked from doing their job',          responseMins: 60,  resolveMins: 600,  aroundClock: false },
  { priority: 'P3', label: 'Normal',   applies: 'Degraded but they can still work',                 responseMins: 240, resolveMins: 1800, aroundClock: false },
  { priority: 'P4', label: 'Low',      applies: 'Request, not a fault — kit, access, nice-to-have', responseMins: 600, resolveMins: 6000, aroundClock: false },
];

export const WORK_CALENDAR = {
  openHour: 7,
  closeHour: 17,
  workingDays: [1, 2, 3, 4, 5], // Mon–Fri
  holidays: ['2026-11-26', '2026-11-27', '2026-12-24', '2026-12-25', '2027-01-01'],
};

// ---------------------------------------------------------- priority rules

// Ordered, first match wins, default pinned last. Conditions read four families
// of fact: the ticket, who raised it, timing, and how many similar tickets
// landed in a window — exactly what evaluate() in rules.js is given.
//
// `firedThisMonth` is demo data standing in for the real count, which the admin
// screen shows beside each rule. Seeing "37 fired / 9 overridden" is what makes
// a wrong rule findable instead of forgotten.
export const PRIORITY_RULES = [
  {
    ruleId: 'r-security', name: 'Security incident', priority: 'P1', sortOrder: 10, enabled: true, pinned: false,
    conditions: [{ fact: 'category', op: 'eq', value: 'security' }],
    because: 'Anything security-shaped goes to the front, every time.',
    firedThisMonth: 3,
  },
  {
    ruleId: 'r-multiple', name: 'Site-wide outage', priority: 'P1', sortOrder: 20, enabled: true, pinned: false,
    conditions: [
      { fact: 'similarCount', op: 'gte', value: 3 },
      { fact: 'othersAffected', op: 'in', value: ['my_team', 'whole_office'] },
    ],
    because: 'Three or more of the same request type in an hour is an outage, not three tickets.',
    firedThisMonth: 4,
  },
  {
    ruleId: 'r-site-down', name: 'Internet down at a location', priority: 'P1', sortOrder: 30, enabled: true, pinned: false,
    conditions: [{ fact: 'requestType', op: 'eq', value: 'network-down' }],
    because: 'A location with no internet cannot dispatch.',
    firedThisMonth: 1,
  },
  {
    ruleId: 'r-leadership', name: 'Exec or GM blocked', priority: 'P2', sortOrder: 40, enabled: true, pinned: false,
    conditions: [{ fact: 'requesterIsLeadership', op: 'eq', value: true }],
    because: 'Contentious on purpose — argue about it in the walkthrough.',
    firedThisMonth: 11,
  },
  {
    ruleId: 'r-team-wide', name: 'A whole team or office affected', priority: 'P2', sortOrder: 50, enabled: true, pinned: false,
    conditions: [{ fact: 'othersAffected', op: 'in', value: ['my_team', 'whole_office'] }],
    because: "One person said it, but it is not one person's problem.",
    firedThisMonth: 18,
  },
  {
    // Widened from one request type to two (2026-08-21) when `st-signin` was
    // added. Same idea, same priority — a ServiceTitan sign-in a tech cannot get
    // past is a lockout by another name, so it belongs on this rule rather than
    // in a near-duplicate of it.
    ruleId: 'r-lockout', name: "Locked out or can't sign in", priority: 'P2', sortOrder: 60, enabled: true, pinned: false,
    conditions: [{ fact: 'requestType', op: 'in', value: ['account-locked', 'st-signin'] }],
    because: 'Nobody locked out can do anything else until it is fixed.',
    firedThisMonth: 22,
  },
  {
    // Without this a broken phone falls through to r-needed-today (P3) or the
    // P3 default, and a tech standing at a job site with a dead handset cannot
    // be dispatched to, cannot be called, and cannot run ServiceTitan. That is
    // the textbook "one person blocked from doing their job" = P2.
    //
    // It reads the request TYPE, not how bad the damage is. `usable` is a form
    // field and deliberately NOT a rule fact — factsFor() in rules.js is an
    // explicit whitelist so a new form field can never silently become a rule
    // input nobody reviewed. If IT wants "barely usable" to score lower, that is
    // a whitelist change to argue for on purpose.
    ruleId: 'r-phone-broken', name: 'Broken or lost phone', priority: 'P2', sortOrder: 65, enabled: true, pinned: false,
    conditions: [{ fact: 'requestType', op: 'eq', value: 'phone-broken' }],
    because: 'A tech with no phone cannot be dispatched to. Argue about whether a cracked-but-working screen deserves the same.',
    firedThisMonth: 0,
  },
  {
    ruleId: 'r-dispatch', name: 'Dispatch, first thing in the morning', priority: 'P2', sortOrder: 70, enabled: true, pinned: false,
    conditions: [
      { fact: 'requesterDepartment', op: 'in', value: ['Dispatch', 'Call Center'] },
      { fact: 'raisedAtLocal', op: 'time_between', value: ['06:00', '08:30'] },
    ],
    because: 'The dispatch window. The same ticket at 2pm is a P3.',
    firedThisMonth: 37,
  },
  {
    // Uses the requests-only fact. Sits ABOVE the hardware rule on purpose: a
    // laptop needed today should not inherit P4 just because it is hardware.
    ruleId: 'r-needed-today', name: 'A request needed today', priority: 'P3', sortOrder: 75, enabled: true, pinned: false,
    conditions: [
      { fact: 'kind', op: 'eq', value: 'request' },
      { fact: 'neededBy', op: 'eq', value: 'today' },
    ],
    because: 'Proposed, not settled — argue about whether "today" should really beat the hardware default.',
    firedThisMonth: 6,
  },
  {
    ruleId: 'r-hardware', name: 'New hardware request', priority: 'P4', sortOrder: 80, enabled: true, pinned: false,
    conditions: [{ fact: 'category', op: 'eq', value: 'hardware' }],
    because: 'Waiting on a manager and a supplier, so it is never the thing to do next.',
    firedThisMonth: 9,
  },
  {
    // Pinned last, cannot be deleted or reordered. Without it a ticket that
    // matches nothing has no priority at all.
    ruleId: 'r-default', name: 'Default — nothing matched', priority: 'P3', sortOrder: 999, enabled: true, pinned: true,
    conditions: [],
    because: 'The safety net. Every ticket lands somewhere.',
    firedThisMonth: 29,
  },
];

// ------------------------------------------------------------------- people

export const IT_STAFF = [
  { id: 'jake',  name: 'Jake Dupaix' },
  { id: 'colby', name: 'Colby Johnson' },
  { id: 'grant', name: 'Grant Myers' },
];

export const WAITING_REASONS = [
  { value: 'requester', label: 'Waiting on requester' },
  { value: 'third_party', label: 'On hold — supplier' },
];
